import { useMemo } from 'react';
import { useAppSelector } from './reduxHook';
import { AddOn } from '../services/renewalsApi';

const SpecialExtrasRegexes = {
	keycare: 'Keycare|key_care',
	breakdownCover: 'Breakdown',
	excessReimbursement: 'Reimbursement',
	guaranteedReplacementHire: 'Hire',
} as const;

type SpecialExtras = {
	-readonly [P in keyof typeof SpecialExtrasRegexes]: AddOn[];
};

type ExtractedAddOns = {
	optionalExtras: AddOn[];
	specialExtras: SpecialExtras;
};

export default function useOptionalExtras() {
	const renewalStore = useAppSelector((redux) => redux.renewal);
	const underwrittenOptions = renewalStore.renewalRatingResponse
		? renewalStore.renewalRatingResponse?.addOns
		: undefined;

	const extras = useMemo((): ExtractedAddOns => {
		// declaring this since it needs to be declared fresh on each hook instance
		// otherwise we share it between all hook instances and end up multiplying the data!
		const initialValues: ExtractedAddOns = {
			specialExtras: {
				keycare: [],
				guaranteedReplacementHire: [],
				excessReimbursement: [],
				breakdownCover: [],
			},
			optionalExtras: [],
		};

		if (!underwrittenOptions) return initialValues;

		return underwrittenOptions.reduce((acc, addon) => {
			for (const [key, regexp] of Object.entries(SpecialExtrasRegexes)) {
				const k = key as keyof SpecialExtras;
				// case insensitive match
				const matched = addon.addOnCode.match(new RegExp(regexp, 'i'));
				if (matched) {
					acc.specialExtras[k].push(addon);

					return acc;
				}
			}

			acc.optionalExtras.push(addon);

			return acc;
			// need to make a copy of initial values, otherwise we'll keep mutating it and end up multiplying the data
		}, initialValues);
	}, [underwrittenOptions]);

	return extras;
}
